<script>
import { mapGetters } from 'vuex';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';

// components
import FormInput from '../../components/Form/Input.vue';
import SubmitButton from '../../components/Button/SubmitButton.vue';

export default {
  components: {
    FormInput,
    SubmitButton,
  },
  props: {
    authError: { type: String, default: '' },
    target: { type: String, default: 'web' },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: {
        email: '',
      },
      loginApi: {
        showLoading: false,
        hasErrored: false,
      },
      csrfToken: '',
    };
  },
  validations: {
    credentials: {
      email: { required, email },
    },
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    logoSrc() {
      return this.globalConfig.logo;
    },
  },
  mounted() {
    this.csrfToken =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') || '';

    this.handleAuthError();
  },
  methods: {
    handleAuthError() {
      if (!this.authError) return;

      useAlert(this.$t('LOGIN.SAML.API.ERROR_MESSAGE'));
      this.loginApi.hasErrored = true;
    },
    onSubmit(event) {
      this.v$.$touch();
      if (this.v$.$invalid) {
        event.preventDefault();
        return;
      }
      this.loginApi.showLoading = true;
      // native form submit (POST to /api/v1/auth/saml_login) proceeds
    },
  },
};
</script>

<template>
  <main
    class="flex flex-col w-full min-h-screen py-20 bg-hichat-25 sm:px-6 lg:px-8 dark:bg-[#181D27]"
  >
    <section
      class="bg-white shadow-lg rounded-xl w-[360px] mx-auto mt-8 px-6 py-6 sm:shadow-lg"
      :class="{ 'animate-wiggle': loginApi.hasErrored }"
    >
      <div class="flex items-center justify-center mb-6">
        <img
          :src="logoSrc"
          :alt="globalConfig.installationName"
          class="h-10 w-auto object-contain"
        />
      </div>
      <h2 class="text-lg font-semibold text-center text-n-gray-9 mb-1">
        {{ $t('LOGIN.SAML.TITLE') }}
      </h2>
      <p class="text-sm text-center text-n-gray-600 mb-6">
        {{ $t('LOGIN.SAML.SUBTITLE') }}
      </p>

      <form
        class="space-y-6"
        method="POST"
        action="/api/v1/auth/saml_login"
        novalidate
        @submit="onSubmit"
      >
        <div class="space-y-2">
          <label class="block text-sm text-gray-600 font-semibold">
            {{ $t('LOGIN.SAML.WORK_EMAIL.LABEL') }}
          </label>
          <FormInput
            v-model="credentials.email"
            name="email"
            type="text"
            class="h-[40px] py-2 px-[14px] !text-n-gray-7 tracking-wide !bg-white"
            autocomplete="email"
            :tabindex="1"
            required
            :placeholder="$t('LOGIN.SAML.WORK_EMAIL.PLACEHOLDER')"
            :has-error="v$.credentials.email.$error"
            @input="v$.credentials.email.$touch"
          />
        </div>
        <input type="hidden" name="authenticity_token" :value="csrfToken" />
        <input type="hidden" name="target" :value="target" />
        <SubmitButton
          :tabindex="2"
          :button-text="$t('LOGIN.SAML.SUBMIT')"
          :loading="loginApi.showLoading"
          :disabled="loginApi.showLoading"
          button-class="!bg-hilab hover:opacity-90 h-[40px] w-full"
        />
      </form>

      <div class="mt-6 text-center">
        <router-link to="/app/login" class="text-n-gray-5 text-sm font-medium">
          {{ $t('LOGIN.SAML.BACK_TO_LOGIN') }}
        </router-link>
      </div>
    </section>
  </main>
</template>
